
import { useTranslation } from "../../i18n/useTranslation";
import styles from './favorite-products.module.scss';
import { Accordion, AccordionDetails, AccordionSummary, Box, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import classNames from 'classnames';
import globalStyles from "../../global-styles.module.scss"
import { FixcenterFilterDto } from '../../components/product-filters-menu/models/fixcenter-filter-dto.model';
import { ProductFilter } from '../../components/product-filters-menu/models/product-filter.model';
import { getFixcenterCategories } from '../../services/products.service';
import { Category } from '../../components/products-page/components/products-categories/models/Category.model';
import { Utils } from '../../shared/constants/utilities.constants';
import { toast } from 'react-toastify';
import { HttpStatusCode } from 'axios';
import { categoryMessageError, getFavoriteProductMessageError, getFavoriteProducts, heightImg, htmlId, widthImg } from './favorite-products.service';
import FirstLevelProductCard from '../../components/first-level-product-card/FirstLevelProductCard';
import { IFavoriteProductsListView } from './interfaces/favorite-products-list-view.interface';
import StarIcon from '@mui/icons-material/Star';
import { Link, useNavigate } from 'react-router-dom';
import RotatingRing from '../../components/rotating-ring-animation/RotatingRing';

const FavoriteProducts: React.FC = () => {
    const { t } = useTranslation('translation');
    const [categories, setMainCategories] = useState<Category[]>([]);
    const [productsRecord, setProductsRecord] = useState<Record<number, IFavoriteProductsListView[]>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const showError = (message: string) => toast.error(message);
    const navigate = useNavigate();

    const handleGetMainCategories = () => {
        const languageCode = localStorage.getItem("lang");
        const fixcenterFilter: FixcenterFilterDto = {
            productFilter: {
                isCategory: true,
                parentProductId: Utils.defaultUuid,
            },
        }
        if (languageCode) {
            fixcenterFilter.languageCode = languageCode;
        }
        getFixcenterCategories(fixcenterFilter)
            .then((response: any) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    setMainCategories(response.data);
                } else {
                    showError(t(categoryMessageError));
                }
        }).catch(() => showError(t(categoryMessageError)));;
    };

    const handleGetFavoriteProducts = (categoryUuid: string, index: number) => {
        const languageCode = localStorage.getItem("lang");
        if (Utils.isNullOrUndefined(productsRecord[index])) {
            setIsLoading(true);
            getFavoriteProducts(categoryUuid, languageCode).then((response: any) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    productsRecord[index] = response.data;
                    setProductsRecord(Utils.clone(productsRecord));
                    setIsLoading(false);
                } else {
                    showError(t(getFavoriteProductMessageError));
                    setIsLoading(false);
                }
            }).catch(() => {
                showError(t(getFavoriteProductMessageError));
                setIsLoading(false);
            });
        }
    };

    const navigateToProduct = (categoryUuid: string, producctUuid: string) => {
        if (categories.some(x => x.id === categoryUuid)) {
            const category: Category = categories.find(x => x.id === categoryUuid) as Category;
            navigate(`/webshop/product/${category.name}/${categoryUuid}/${producctUuid}`);
        }
    }

    useEffect(() => {
        handleGetMainCategories();
    }, []);

    return (
        <div className={styles.container}>
            {isLoading && <RotatingRing />}
            <label className={classNames(styles.main_header_text, globalStyles.flex_center_item, globalStyles.mb_1)}>
                {t("MY_FAVORITE_PRODUCTS")}
            </label>
            <div className={classNames(styles.content, globalStyles.flex_center_item)}>
                <Box
                    sx={{
                        borderTop: '1px solid #dbdbdb',
                        height: 'calc(100vh - 215px)',
                        overflow: 'auto',
                        minWidth: '100%'
                    }}>
                    {categories.map((category, categoryIndex) => (
                     <Accordion
                        key={categoryIndex}
                        tabIndex={categoryIndex}
                        onChange={(_, expanded) => {
                            if (expanded) {
                                handleGetFavoriteProducts(category.id, categoryIndex);
                            }
                        }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box
                                sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                }}>
                                <Typography variant="subtitle1">
                                    <FirstLevelProductCard category={category} heightImg={heightImg} widthImg={widthImg}/>
                                </Typography>
                            </Box>
                        </AccordionSummary>
                            <AccordionDetails className={styles.schema_values}>
                                {productsRecord[categoryIndex] &&
                                    productsRecord[categoryIndex].map((product, productIndex) => (
                                    <div
                                        key={productIndex}
                                        className={globalStyles.mb_2}>
                                        <Tooltip
                                            placement={'right'}
                                            title={t('GO_TO_PRODUCT')}>
                                            <button
                                                id = {`${htmlId}-go-to-product-${categoryIndex}-${productIndex}-btn`}
                                                className={globalStyles.default_button_style}
                                                onClick={() => navigateToProduct(category.id, product.productUuid)}>
                                                <label className={styles.main_product_header}>
                                                    {product.name}
                                                </label>
                                            </button>
                                        </Tooltip>
                                        {product.variants && product.variants.map((variant, variantIndex) => (
                                            <div 
                                                key={variantIndex}
                                                className={globalStyles.ml_3}>
                                                <StarIcon className={classNames(styles.star_icon, globalStyles.mr_1)}></StarIcon>
                                                <label className={styles.variant_product_header}>
                                                    {variant.name}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            }
                            {productsRecord[categoryIndex] && productsRecord[categoryIndex].length === 0 && 
                                <label className={styles.text}>{t("NO_FAVORITE_PRODUCTS")}</label>
                            }
                        </AccordionDetails>
                    </Accordion>                                
                    ))}
                </Box>
            </div>
        </div>
    );
};

export default FavoriteProducts;